<template>
  <v-row class="d-flex align-center">
    <v-col cols="12" sm="8" md="9" :class="$vuetify.display.xs ? 'py-2 pr-1' : 'py-0'">
      <v-slider
        always-dirty
        v-model="sliderAmount"
        @update:model-value="onSliderInput"
        @focus="showToolTip = true"
        @blur="showToolTip = false"
        :aria-labelledby="labelId"
        :hide-details="true"
      />
    </v-col>
    <v-col cols="12" sm="4" md="3" class="mt-0" :class="$vuetify.display.xs ? 'pl-5 pb-5' : 'py-0'">
      <v-tooltip
        v-model="showToolTip"
        :contained="true"
        :location="$vuetify.display.xs ? 'bottom' : 'top'"
        class="mx-6"
        max-width="auto"
        :open-on-hover="false"
        :open-on-focus="true"
      >
        <template #activator="{ props }">
          <CurrencyDisplay
            v-model="amount"
            :max-length="6"
            @update:model-value="onTextInput"
            @focus="showToolTip = true"
            @blur="showToolTip = false"
            :notify="notifyUser"
            :label-id="labelId"
            :tool-tip-id="toolTipId"
            :hide-details="true"
            v-bind="props"
          />
        </template>
        <span role="tooltip" :id="toolTipId">
          {{ tooltipText }}
        </span>
      </v-tooltip>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import Slider from '@/components/Inputs/Slider/Slider.vue';
import CurrencyDisplay from './CurrencyDisplay.vue';
import { defineComponent, type ComponentOptions } from 'vue';

const componentOptions: ComponentOptions = {
  name: 'LinkedSlider',
  components: {
    Slider,
    CurrencyDisplay,
  },
  props: {
    modelValue: {
      type: [Number, String],
      required: true,
      default: 0,
    },
    min: {
      type: [Number, String],
      required: false,
      default: 0,
    },
    max: {
      type: [Number, String],
      required: false,
      default: 100,
    },
    step: {
      type: [Number, String],
      required: false,
      default: 1,
    },
    tooltipText: {
      type: String,
      required: false,
      default: undefined,
    },
    labelId: {
      type: String,
      required: false,
      default: () => '',
    },
  },
  data: () => ({
    amount: 0,
    sliderAmount: 0,
    showToolTip: false,
    notifyUser: true,
  }),
  computed: {
    toolTipId() {
      return `tool_tip_${this.labelId}`;
    },
  },
  methods: {
    setSliderPosition(value: number) {
      if (value >= +this.max) {
        this.sliderAmount = 100;
        return;
      }
      if (value <= +this.min) {
        this.sliderAmount = 0;
        return;
      }
      this.sliderAmount = Math.floor((value - +this.min) / +this.step);
    },
    onSliderInput(value: number) {
      this.amount = (+this.min + Math.floor(((value / 100) * +this.max) / +this.step)) * +this.step;
      this.$emit('update:modelValue', this.amount);
      this.$emit('input');
    },
    onTextInput(value: number) {
      this.setSliderPosition(value);
      this.$emit('update:modelValue', value);
      this.$emit('input');
    },
  },
  mounted() {
    this.setSliderPosition(this.modelValue);
    this.amount = this.modelValue;
  },
  watch: {
    modelValue(value) {
      this.amount = value;
      this.setSliderPosition(value);
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.hidden-tablet-and-down,
.hidden-desktop-and-up {
  display: none;
}
.hidden-tablet-and-down {
  @media only screen and (min-width: 1024px) {
    display: flex;
  }
}
.hidden-desktop-and-up {
  @media only screen and (max-width: 1023px) {
    display: flex;
  }
}
.slider-tooltip {
  @media only screen and (max-width: 1023px) {
    margin-top: -48px !important;
  }
  @media only screen and (max-width: 767px) {
    margin-top: 0 !important;
  }
}
:deep(.v-text-field input) {
  padding: 0 !important;
}
</style>
